import { PANEL_DEFINITION_MAP, PanelComponentId } from './panelRegistry';

export type PanelTree = PanelSplitNode | PanelLeafNode;

export interface PanelLeafNode {
  id: string;
  type: 'leaf';
  componentId: PanelComponentId;
  size?: number;
}

export interface PanelSplitNode {
  id: string;
  type: 'split';
  direction: 'horizontal' | 'vertical';
  children: [PanelTree, PanelTree];
  size?: number;
}

export function isLeaf(node: PanelTree): node is PanelLeafNode {
  return node.type === 'leaf';
}

export function createLeaf(componentId: PanelComponentId, id: string, size?: number): PanelLeafNode {
  if (!PANEL_DEFINITION_MAP[componentId]) {
    throw new Error(`Unknown panel component: ${componentId}`);
  }
  return { id, type: 'leaf', componentId, size };
}

export function updatePanel(
  tree: PanelTree,
  panelId: string,
  updater: (node: PanelTree) => PanelTree,
): PanelTree {
  if (tree.id === panelId) {
    return updater(tree);
  }

  if (tree.type === 'split') {
    return {
      ...tree,
      children: [
        updatePanel(tree.children[0], panelId, updater),
        updatePanel(tree.children[1], panelId, updater),
      ],
    };
  }

  return tree;
}

export function replaceLeafComponent(
  tree: PanelTree,
  panelId: string,
  componentId: PanelComponentId,
): PanelTree {
  return updatePanel(tree, panelId, (node) => {
    if (isLeaf(node)) {
      return { ...node, componentId };
    }
    return node;
  });
}

function withSize(node: PanelTree, size: number): PanelTree {
  if (isLeaf(node)) {
    return { ...node, size };
  }
  return { ...node, size, children: [clonePanelTree(node.children[0]), clonePanelTree(node.children[1])] as [PanelTree, PanelTree] };
}

export function splitPanel(
  tree: PanelTree,
  panelId: string,
  direction: 'horizontal' | 'vertical',
  createId: () => string,
): PanelTree {
  return updatePanel(tree, panelId, (node) => {
    if (!isLeaf(node)) return node;

    const childA: PanelLeafNode = {
      id: createId(),
      type: 'leaf',
      componentId: node.componentId,
      size: 50,
    };
    const childB: PanelLeafNode = {
      id: createId(),
      type: 'leaf',
      componentId: node.componentId,
      size: 50,
    };

    return {
      id: node.id,
      type: 'split',
      direction,
      size: node.size,
      children: [childA, childB],
    } satisfies PanelSplitNode;
  });
}

function removeFromNode(node: PanelTree, panelId: string): PanelTree | null {
  if (isLeaf(node)) {
    return node.id === panelId ? null : node;
  }

  const left = removeFromNode(node.children[0], panelId);
  const right = removeFromNode(node.children[1], panelId);

  if (!left && !right) return null;
  if (!left && right) {
    return withSize(right, node.size ?? 100);
  }
  if (!right && left) {
    return withSize(left, node.size ?? 100);
  }

  return {
    ...node,
    children: [left!, right!] as [PanelTree, PanelTree],
  } as PanelSplitNode;
}

export function removePanel(tree: PanelTree, panelId: string): PanelTree {
  const next = removeFromNode(tree, panelId);
  if (!next) {
    // Ensure we always keep a leaf in view.
    return createLeaf('sessions', tree.id, 100);
  }
  return next;
}

export function countLeaves(node: PanelTree): number {
  if (isLeaf(node)) return 1;
  return node.children.reduce((total, child) => total + countLeaves(child), 0);
}

export function clonePanelTree(node: PanelTree): PanelTree {
  if (isLeaf(node)) {
    return { ...node };
  }
  return {
    ...node,
    children: [clonePanelTree(node.children[0]), clonePanelTree(node.children[1])] as [PanelTree, PanelTree],
  } as PanelSplitNode;
}

export function swapLeafContent(tree: PanelTree, panelAId: string, panelBId: string): PanelTree {
  if (panelAId === panelBId) {
    return tree;
  }

  let leafA: PanelLeafNode | null = null;
  let leafB: PanelLeafNode | null = null;

  function findLeaves(node: PanelTree) {
    if (isLeaf(node)) {
      if (node.id === panelAId) {
        leafA = node;
      } else if (node.id === panelBId) {
        leafB = node;
      }
      return;
    }
    findLeaves(node.children[0]);
    findLeaves(node.children[1]);
  }

  findLeaves(tree);

  if (!leafA || !leafB) {
    return tree;
  }

  const swapA = { componentId: leafA.componentId, size: leafA.size };
  const swapB = { componentId: leafB.componentId, size: leafB.size };

  const withFirst = updatePanel(tree, panelAId, (node) => {
    if (isLeaf(node)) {
      return {
        ...node,
        componentId: swapB.componentId,
        size: swapB.size,
      };
    }
    return node;
  });

  return updatePanel(withFirst, panelBId, (node) => {
    if (isLeaf(node)) {
      return {
        ...node,
        componentId: swapA.componentId,
        size: swapA.size,
      };
    }
    return node;
  });
}
